/* ComplimentaryProducts.jsx - Manage Ödenmez product catalog (EUR only, TL auto via TCMB) */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, Edit2, Search, Wine, Coffee, UtensilsCrossed, Package, ShoppingBag } from 'lucide-react';
import { formatTRNumber } from '../lib/utils';

const CATEGORIES = [
    { value: 'alkollu', label: 'Alkollü', icon: Wine, color: 'bg-rose-100 text-rose-700 border-rose-200' },
    { value: 'soft', label: 'Soft İçecek', icon: Coffee, color: 'bg-sky-100 text-sky-700 border-sky-200' },
    { value: 'yemek', label: 'Yemek', icon: UtensilsCrossed, color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { value: 'diger', label: 'Diğer', icon: Package, color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

export default function ComplimentaryProducts() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [filterCat, setFilterCat] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [tcmbRate, setTcmbRate] = useState(35.5);
    const [form, setForm] = useState({ name: '', category: 'alkollu', unit_price_eur: '' });

    useEffect(() => { fetchProducts(); fetchRate(); }, []);

    const fetchRate = async () => {
        try {
            const res = await api.get('/tcmb-rate');
            if (res.data?.rate) setTcmbRate(res.data.rate);
        } catch { /* default */ }
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterCat !== 'all') params.category = filterCat;
            if (search) params.q = search;
            const res = await api.get('/complimentary/products', { params });
            setProducts(res.data || []);
        } catch {
            toast.error('Ürünler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProducts(); /* eslint-disable-next-line */ }, [filterCat]);

    const openCreate = () => {
        setEditing(null);
        setForm({ name: '', category: 'alkollu', unit_price_eur: '' });
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({ name: p.name, category: p.category, unit_price_eur: p.unit_price_eur });
        setDialogOpen(true);
    };

    const save = async () => {
        if (!form.name.trim()) {
            toast.error('Ürün adı gerekli');
            return;
        }
        try {
            const payload = {
                name: form.name.trim(),
                category: form.category,
                unit_price_eur: parseFloat(String(form.unit_price_eur).replace(',', '.')) || 0,
            };
            if (editing) {
                await api.put(`/complimentary/products/${editing.id}`, payload);
                toast.success('Güncellendi');
            } else {
                await api.post('/complimentary/products', payload);
                toast.success('Eklendi');
            }
            setDialogOpen(false);
            fetchProducts();
        } catch {
            toast.error('Kaydetme başarısız');
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/complimentary/products/${id}`);
            toast.success('Silindi');
            fetchProducts();
        } catch {
            toast.error('Silme başarısız');
        }
    };

    const filtered = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="space-y-4" data-testid="comp-products-page">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigate('/odenmez')}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                    </Button>
                    <ShoppingBag className="w-6 h-6 text-slate-600" />
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-slate-800">Ödenmez Ürün Katalogu</h1>
                        <p className="text-xs text-slate-500">EUR fiyat tanımlanır, TL TCMB kuru ile otomatik hesaplanır (Güncel: 1 € ≈ {formatTRNumber(tcmbRate, 4)} ₺)</p>
                    </div>
                </div>
                <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" /> Yeni Ürün
                </Button>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Ürünler ({filtered.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-2 items-end">
                        <div className="flex-1 min-w-[200px] relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün ara..." className="pl-8 h-10" data-testid="product-search" />
                        </div>
                        <Select value={filterCat} onValueChange={setFilterCat}>
                            <SelectTrigger className="h-10 w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-100 border-b">
                                <tr>
                                    <th className="text-left p-3 font-medium text-slate-600">Ürün</th>
                                    <th className="text-left p-3 font-medium text-slate-600">Kategori</th>
                                    <th className="text-right p-3 font-medium text-slate-600">Birim Fiyat (€)</th>
                                    <th className="text-right p-3 font-medium text-slate-600">Birim Fiyat (₺ ~)</th>
                                    <th className="text-center p-3 font-medium text-slate-600">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="p-6 text-center text-slate-400">Yükleniyor...</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan="5" className="p-6 text-center text-slate-400">Ürün yok</td></tr>
                                ) : filtered.map(p => {
                                    const cat = CATEGORIES.find(c => c.value === p.category);
                                    const Icon = cat?.icon || Package;
                                    return (
                                        <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                                            <td className="p-3 font-medium">{p.name}</td>
                                            <td className="p-3">
                                                <Badge variant="outline" className={cat?.color}>
                                                    <Icon className="w-3.5 h-3.5 mr-1 inline" />
                                                    {cat?.label || p.category}
                                                </Badge>
                                            </td>
                                            <td className="p-3 text-right font-semibold text-emerald-700">{formatTRNumber(p.unit_price_eur, 2)} €</td>
                                            <td className="p-3 text-right text-amber-700">{formatTRNumber(p.unit_price_eur * tcmbRate, 2)} ₺</td>
                                            <td className="p-3 text-center">
                                                <div className="flex justify-center gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Edit2 className="w-4 h-4" /></Button>
                                                    <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Ürün Düzenle' : 'Yeni Ürün'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Ürün Adı *</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Efes Pilsen" data-testid="form-product-name" />
                        </div>
                        <div>
                            <Label>Kategori *</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="form-product-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Birim Fiyat (€)</Label>
                            <Input type="text" inputMode="decimal" value={form.unit_price_eur} onChange={(e) => setForm({ ...form, unit_price_eur: e.target.value })} placeholder="0,00" data-testid="form-product-price" />
                            <p className="text-xs text-slate-400 mt-1">≈ {formatTRNumber((parseFloat(String(form.unit_price_eur).replace(',', '.')) || 0) * tcmbRate, 2)} ₺ (TCMB)</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
                        <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="form-product-save">{editing ? 'Güncelle' : 'Ekle'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
